import React from 'react';
import { withStyles, withTheme } from '@glu/theming';
import {
  CheckmarkCircleNextIcon,
  CheckmarkCircleIcon,
  XCircleIcon,
  ExclamationCircleNextIcon,
  ExclamationTriangleNextIcon,
  InfoCircleNextIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InfoCircleIcon
} from '@glu/icons-react';
import { Button, TERTIARY } from '@glu/buttons-react';
import locale from '@glu/locale';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import styles from './Dialog.styles';

const icons = {
  ash: {
    success: CheckmarkCircleNextIcon,
    error: ExclamationTriangleNextIcon,
    warning: ExclamationCircleNextIcon,
    info: InfoCircleNextIcon
  },
  legacy: {
    success: CheckmarkCircleIcon,
    error: ExclamationCircleIcon,
    warning: ExclamationTriangleIcon,
    info: InfoCircleIcon
  }
};

const Dialog = ({
  children,
  classes,
  header,
  footer,
  theme: {
    name
  },
  suppressIcon,
  variant,
  closeModal,
  className,
  dataQa
}) => {
  const Icon = (icons[name] && icons[name][variant]) || (name === 'legacy' ? () => null : InfoCircleNextIcon);

  return (
    <article className={classNames(classes.dialog, 'dialog', className)} role="document" data-qa={dataQa}>
      <div className={classNames(classes.content, classes[variant])}>
        {header ? (
          <header className={classes.header}>
            <Button
              variant={TERTIARY}
              className={classes.close}
              onClick={closeModal}
              aria-label={locale.get('close')}
            >
              <XCircleIcon />
            </Button>
            {suppressIcon ? null : <Icon className={classes.icon} />}
            {typeof header === 'string'
              ? <h1 data-qa="modal-header" className={classes.title}>{header}</h1>
              : header}
          </header>
        ) : null}

        <section className={classes.modalBody}>
          <div data-qa="modal-body">{children}</div>
        </section>

        {footer && <footer className={classes.modalFooter}>{footer}</footer>}
      </div>
    </article>
  );
};

Dialog.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  classes: PropTypes.objectOf(PropTypes.string),
  footer: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.string
  ]),
  header: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.string
  ]),
  suppressIcon: PropTypes.bool,
  theme: PropTypes.objectOf(PropTypes.string),
  variant: PropTypes.oneOf(['info', 'success', 'error', 'warning']),
  closeModal: PropTypes.func.isRequired,
  className: PropTypes.string,
  dataQa: PropTypes.string
};

Dialog.defaultProps = {
  children: [],
  classes: {},
  footer: null,
  header: null,
  // istanbul ignore next
  suppressIcon: false,
  theme: {},
  variant: null,
  className: '',
  dataQa: 'modal'
};

export const DialogBase = Dialog;
export default withTheme(withStyles(styles)(Dialog));
